/**
 * Composition root: the only place that knows every module and every browser
 * adapter. Modules themselves stay host-agnostic, and tests wire the same
 * modules with fakes instead.
 */
import { headlessBackendFactory } from "../render/HeadlessRenderBackend";
import { AssetsModule } from "../assets/AssetsModule";
import { CommandsModule } from "../commands/CommandsModule";
import { SystemClock } from "../core/Clock";
import { CoreModule } from "../core/CoreModule";
import { Engine } from "../core/Engine";
import { EditorModule } from "../editor/EditorModule";
import { THEME_STORAGE_KEY } from "../editor/theme/themeBoot";
import { ThemeModule } from "../editor/theme/ThemeModule";
import { documentThemeTarget } from "../editor/theme/ThemeService";
import type { RenderBackendFactory } from "../render/RenderBackend";
import { RendererModule, threeBackendFactory } from "../render/RendererModule";
import { qualityFor, qualityFromQuery } from "../render/quality";
import type { RenderQuality } from "../render/quality";
import { WorldModule } from "../world/WorldModule";
import { EDITOR_CONFIG } from "./config";
import { SAVE_KEY_PREFIX } from "../editor/levels/LevelSaves";
import type { SaveStorage } from "../editor/levels/LevelSaves";
import { systemTimers } from "../core/Timers";

/** Every module the editor boots, in declaration order. */
export const EDITOR_MODULES = [
  CoreModule,
  CommandsModule,
  AssetsModule,
  RendererModule,
  ThemeModule,
  WorldModule,
  EditorModule,
] as const;

/** localStorage key for persisted editor settings (camera speed, show flags, …). */
export const SETTINGS_STORAGE_KEY = "kn.settings";

export function createEditorEngine(win: Window, doc: Document): Engine {
  const engine = new Engine({ clock: new SystemClock(), version: EDITOR_CONFIG.version });

  engine.configure(RendererModule, {
    createBackend: backendFromQuery(new URLSearchParams(win.location.search), deviceQuality(win)),
  });
  engine.configure(ThemeModule, {
    target: documentThemeTarget(doc.documentElement),
    readStoredId: () => win.localStorage.getItem(THEME_STORAGE_KEY),
    writeStoredId: (id) => {
      win.localStorage.setItem(THEME_STORAGE_KEY, id);
    },
    prefersLight: () => win.matchMedia("(prefers-color-scheme: light)").matches,
    watchPrefersLight: (listener) => {
      const query = win.matchMedia("(prefers-color-scheme: light)");
      const onChange = () => {
        listener(query.matches);
      };
      query.addEventListener("change", onChange);
      return () => {
        query.removeEventListener("change", onChange);
      };
    },
  });
  engine.configure(EditorModule, {
    restoredSettings: readJson(win, SETTINGS_STORAGE_KEY),
    reducedMotion: win.matchMedia("(prefers-reduced-motion: reduce)").matches,
    saveStorage: browserSaveStorage(win),
    timers: systemTimers,
    // Missing outside secure contexts; copies then stay in the editor.
    clipboard: (win.navigator.clipboard as Clipboard | undefined) ?? null,
  });

  return engine;
}

/**
 * `?rhi=webgl` forces three's WebGL2 backend. `?rhi=none` simulates a machine
 * with no GPU API, to exercise the Null-backend fallback (R-1). `?rhi=null` is
 * the null renderer, Unreal's -nullrhi: the editor in full, nothing drawn (CI's
 * UI tests run on it).
 */
function backendFromQuery(params: URLSearchParams, quality: RenderQuality): RenderBackendFactory {
  const rhi = params.get("rhi");
  if (rhi === "none") return () => Promise.reject(new Error("disabled by ?rhi=none"));
  if (rhi === "null") return headlessBackendFactory();

  return threeBackendFactory({ forceWebGL: rhi === "webgl", quality });
}

/**
 * What this device can afford to draw: mobile quality where the main pointer
 * is a finger (phones and tablets, with or without a stylus), high elsewhere.
 * By the device rather than the layout, so a desktop window that's narrow at
 * load still draws at high. `?quality=high|mobile` overrides it.
 */
function deviceQuality(win: Window): RenderQuality {
  return qualityFor({
    touchFirst: win.matchMedia("(pointer: coarse)").matches,
    requested: qualityFromQuery(win.location.search),
  });
}

/** Reads a JSON object from storage; missing, blocked or malformed storage yields {}. */
/** localStorage as a save store, or null when the browser won't give it (privacy modes). */
function browserSaveStorage(win: Window): SaveStorage | null {
  try {
    const storage = win.localStorage;
    storage.getItem(SAVE_KEY_PREFIX);
    return {
      getItem: (key) => storage.getItem(key),
      setItem: (key, value) => {
        storage.setItem(key, value);
      },
      removeItem: (key) => {
        storage.removeItem(key);
      },
      keys: () =>
        Array.from({ length: storage.length }, (_, i) => storage.key(i)).filter(
          (key): key is string => key !== null,
        ),
    };
  } catch {
    return null;
  }
}

function readJson(win: Window, key: string): Record<string, unknown> {
  try {
    const parsed: unknown = JSON.parse(win.localStorage.getItem(key) ?? "{}");
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}
